import { Component, Suspense, lazy, useEffect, useState, type ComponentProps, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQuery as useApolloQuery } from "@apollo/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { useAuth } from "@/lib/auth";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";
import { TourKeyEnum, TourStatusEnum } from "@/__generated__/graphql";
import { isChunkLoadError } from "@/lib/chunkReload";
import { captureBoundaryError } from "@/lib/sentry";
import { IMPERSONATION_QUERY } from "@/components/ImpersonationBanner";
import { canAutoStart, canContinue, TOUR_RESUME_STEP_KEY, TOUR_SESSION_SUPPRESS_KEY, TOUR_VERSION, type TourKey } from "./constants";
import { onTourStartRequest } from "./tourBus";
// Type only, erased at build: the engine itself stays in its own lazy chunk (spec §3.1).
import type TourEngine from "./TourEngine";

const LazyTourEngine = lazy(() => import("./TourEngine"));

const TOUR_FLAG = "interactive_tour";
const BOOTSTRAP_STALE_MS = 5 * 60 * 1000;
const FINISHED_NOTE_MS = 5000;

const TOUR_BOOTSTRAP = graphql(`
  query TourBootstrap($tourKey: TourKeyEnum!) {
    me {
      id
      googleCalendarAvailable
      tourState(tourKey: $tourKey) { tourKey status version }
    }
  }
`);
type BootstrapAnswer = ResultOf<typeof TOUR_BOOTSTRAP>;

// How the engine says a run ended, derived from it so a new outcome is a compile error here.
type TourOutcome = Parameters<ComponentProps<typeof TourEngine>["onClose"]>[0];

// The schema's enum values are UPPER_CASE; the tour keys the app uses are lowercase. Mapped at the network
// boundary through the generated enums (the engine does the same for what it sends): a tour key or an outcome
// added without a wire name is a compile error, not an invalid request.
const TOUR_KEY_WIRE = { student: TourKeyEnum.Student, tutor: TourKeyEnum.Tutor } satisfies Record<TourKey, TourKeyEnum>;
const STATUS_WIRE: Record<Exclude<TourOutcome, "dismissed">, TourStatusEnum> = {
  completed: TourStatusEnum.Completed,
  skipped: TourStatusEnum.Skipped,
};

// The welcome card is offered by itself at most once per page load, however that run ends: a finished or skipped
// tour whose write never reached the server, a "not now" in a browser that kept no mark of it, or a run that
// crashed must not come straight back. A fresh page load asks the server (and the session mark) again.
const autoStartSpent = new Set<string>();

// Test hook: module state is per page load, so specs simulate a fresh one.
export function resetTourAutoStart() {
  autoStartSpent.clear();
}

function readResumeAt(): number {
  try {
    const stored = localStorage.getItem(TOUR_RESUME_STEP_KEY);
    const step = stored === null ? 0 : Number.parseInt(stored, 10);
    return Number.isFinite(step) && step >= 0 ? step : 0;
  } catch {
    return 0; // private mode
  }
}

function sessionSuppressed(): boolean {
  try {
    return sessionStorage.getItem(TOUR_SESSION_SUPPRESS_KEY) === "1";
  } catch {
    return false; // private mode
  }
}

// TourManager sits outside the app's only ErrorBoundary (AppShell wraps just AppRoutes), so without this a
// failed tour chunk, or a crash in the tour, would unmount the whole root. A broken tour degrades to no tour,
// never a blank app (spec §3.1). It lives only as long as the gate it wraps, so it starts fresh on the next
// visit to a page with the tour.
class TourBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A tab that outlived a deploy asks for a chunk that is gone: lib/chunkReload.ts is already reloading it
    // onto the new build, and that is not a bug in the tour.
    if (isChunkLoadError(error)) return;
    captureBoundaryError(error, info);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function TourGate({
  userId,
  tourKey,
  onEngineChange,
}: {
  userId: string;
  tourKey: TourKey;
  onEngineChange: (mounted: boolean) => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const { flags, isLoading: flagsLoading } = useFeatureFlags();
  const [running, setRunning] = useState<{ resumeAt: number } | null>(null);
  const [finished, setFinished] = useState(false);

  const runKey = `${userId}:${tourKey}`;
  // Per user: sign-out only empties ["me"], so an answer keyed by the tour alone would be handed to whoever
  // signs in next on the same page (dev account switching).
  const bootstrapKey = ["tourBootstrap", userId, tourKey] as const;
  // The kill switch: off (or not yet known) means the tour asks nothing and starts nothing.
  const switchOn = !flagsLoading && flags.includes(TOUR_FLAG);

  const bootstrap = useQuery({
    queryKey: bootstrapKey,
    queryFn: () => graphqlClient.request(TOUR_BOOTSTRAP, { tourKey: TOUR_KEY_WIRE[tourKey] }),
    enabled: switchOn,
    staleTime: BOOTSTRAP_STALE_MS,
  });
  const me = bootstrap.data?.me ?? null;
  const stored = me?.tourState;
  const due = me !== null && (!stored || stored.version < TOUR_VERSION[tourKey]);

  // A staff member clicking through as a user must never be shown, or be able to mark done, that user's tour.
  // The banner's own question, so the answer usually comes from its cache with no request of its own. Nothing
  // auto-starts until it says "no": an unanswered or failed question is an unknown, not a no.
  const impersonation = useApolloQuery(IMPERSONATION_QUERY, { fetchPolicy: "cache-first", skip: !switchOn });
  const notImpersonating = impersonation.data?.impersonation.active === false;

  // Manual start (Help menu): the kill switch still applies; the session mark and a stored outcome do not.
  useEffect(
    () => onTourStartRequest(() => {
      if (switchOn) setRunning((current) => current ?? { resumeAt: 0 });
    }),
    [switchOn],
  );

  // Auto-start (spec §3.1): the tour is due, nothing has asked to be left alone, and this is a page it may begin on.
  useEffect(() => {
    if (running || !switchOn || !due || !notImpersonating) return;
    if (!canAutoStart(pathname) || sessionSuppressed() || autoStartSpent.has(runKey)) return;
    autoStartSpent.add(runKey);
    setRunning({ resumeAt: readResumeAt() });
  }, [running, switchOn, due, notImpersonating, pathname, runKey]);

  // While the engine is on screen the manager keeps this gate mounted on any route: the engine ends itself, the
  // way Esc does, when the route stops being one a tour may run on (spec §3.1), and it can only do that if it is
  // still being rendered when the location changes.
  const engineMounted = running !== null && me !== null;
  useEffect(() => {
    onEngineChange(engineMounted);
    return () => onEngineChange(false);
  }, [engineMounted, onEngineChange]);

  // "Tour finished." for a few seconds, in a region that is already on the page while the tour runs (text
  // written into an existing polite region is what a screen reader announces). It lives here rather than in the
  // engine because the engine unmounts in the same update as its onClose.
  useEffect(() => {
    if (!finished) return;
    const timer = window.setTimeout(() => setFinished(false), FINISHED_NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [finished]);

  const handleClose = (outcome: TourOutcome) => {
    if (outcome !== "dismissed") {
      // The engine has just sent the outcome to the server and does not wait for it. Until a read could see it,
      // this is the truth the page has: the cached answer still says "no row", and the auto-start decision runs
      // again the moment the engine unmounts. Written first, synchronously, before anything re-renders.
      queryClient.setQueryData<BootstrapAnswer>(bootstrapKey, (old) =>
        old?.me
          ? {
              ...old,
              me: { ...old.me, tourState: { tourKey: TOUR_KEY_WIRE[tourKey], status: STATUS_WIRE[outcome], version: TOUR_VERSION[tourKey] } },
            }
          : old,
      );
      // Marked stale, so the next visit reconciles with the server, but not refetched now: that read would race
      // the engine's own write, could answer "no row", and would overwrite the line above.
      void queryClient.invalidateQueries({ queryKey: bootstrapKey, refetchType: "none" });
    }
    setRunning(null);
    if (outcome === "completed") setFinished(true);
  };

  return (
    <>
      {(running || finished) && (
        <div aria-live="polite" className="sr-only">
          {finished ? t("tour.common.finished") : ""}
        </div>
      )}
      {running && me && (
        <Suspense fallback={null}>
          <LazyTourEngine
            tourKey={tourKey}
            ctx={{ googleCalendarAvailable: me.googleCalendarAvailable }}
            resumeAt={running.resumeAt}
            onClose={handleClose}
          />
        </Suspense>
      )}
    </>
  );
}

export function TourManager() {
  // Nothing on the server and on the first client render (the two must match, #921); the effect flips it once
  // the page is hydrated.
  const [live, setLive] = useState(false);
  useEffect(() => setLive(true), []);
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [engineMounted, setEngineMounted] = useState(false);

  const tourKey: TourKey | null = user?.role === "tutor" ? "tutor" : user?.role === "student" ? "student" : null;

  // Cheap global state only, and no query of its own: a public page, or anyone without a tour, pays nothing.
  // The gate mounts on a route where a tour may continue, or wherever the engine is already on screen.
  if (!live || !user || !tourKey || !(canContinue(pathname) || engineMounted)) return null;

  return (
    <TourBoundary key={`${user.id}:${tourKey}`}>
      <TourGate userId={user.id} tourKey={tourKey} onEngineChange={setEngineMounted} />
    </TourBoundary>
  );
}
